import {
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  FileX,
  Quote,
  Search,
  X
} from "lucide-react";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { MetaLine } from "@/components/ui/meta-line";
import { PaneHeader } from "@/components/ui/pane-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useCopyCitation } from "../hooks/useCopyCitation";
import { useLibraryDocument, usePassages } from "../hooks/useResearch";
import {
  findAll,
  locate,
  segmentsOf,
  toReaderText,
  type Range,
  type ReaderText
} from "../readerText";
import { courtLabel } from "../sources";
import type { LibraryPassage } from "../types";
import { errorText } from "./SourceResults";
import { WeightBadge } from "./WeightBadge";

const MARK: Record<string, string> = {
  match: "rounded-xs bg-warn-soft text-fg",
  active: "rounded-xs bg-warn-soft text-fg ring-1 ring-warn-line",
  passage: "bg-accent-soft text-fg"
};

type BodyProps = {
  reader: ReaderText;
  matches: Range[];
  active: number;
  passage: Range | null;
};

/** The document's paragraphs, with find matches and the jumped-to passage marked. */
const ReaderBody: FC<BodyProps> = memo(
  ({ reader, matches, active, passage }) => (
    <article className="mx-auto flex w-full max-w-170 flex-col gap-3 px-6 pt-2 pb-24 text-md text-fg">
      {reader.paragraphs.map((_, index) => {
        const segments = segmentsOf(reader, index, matches, active, passage);
        const passageStart =
          passage &&
          passage[0] >= reader.starts[index] &&
          passage[0] < reader.starts[index] + reader.paragraphs[index].length;
        return (
          <p key={index} className="break-words">
            {segments.map((segment, i) => {
              if (!segment.mark) return <span key={i}>{segment.text}</span>;
              const first =
                passageStart &&
                segments.findIndex((s) => s.mark === "passage") === i;
              return (
                <mark
                  key={i}
                  className={MARK[segment.mark]}
                  data-match={segment.match}
                  data-passage={first ? "start" : undefined}
                >
                  {segment.text}
                </mark>
              );
            })}
          </p>
        );
      })}
    </article>
  )
);
ReaderBody.displayName = "ReaderBody";

type Props = {
  docId: string;
  /** The search that found the document; its passages are offered first. */
  query: string;
  onClose: () => void;
};

/** A library document, whole: what it is worth, its passages on the search, and find. */
export const DocumentReader: FC<Props> = ({ docId, query, onClose }) => {
  const doc = useLibraryDocument(docId);
  const passages = usePassages(query, docId);
  const copy = useCopyCitation();
  const scroller = useRef<HTMLDivElement>(null);
  const [find, setFind] = useState("");
  const [active, setActive] = useState(0);
  const [passage, setPassage] = useState<Range | null>(null);

  const reader = useMemo(
    () => toReaderText(doc.data?.text ?? ""),
    [doc.data?.text]
  );
  const matches = useMemo(() => findAll(reader.flat, find), [reader, find]);

  useEffect(() => {
    setFind("");
    setPassage(null);
    scroller.current?.scrollTo({ top: 0 });
  }, [docId]);
  useEffect(() => setActive(0), [find]);
  useEffect(() => {
    if (!matches.length) return;
    scroller.current
      ?.querySelector(`[data-match="${active}"]`)
      ?.scrollIntoView({ block: "center" });
  }, [active, matches]);
  useEffect(() => {
    if (!passage) return;
    scroller.current
      ?.querySelector('[data-passage="start"]')
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [passage]);

  const step = (by: number) =>
    matches.length &&
    setActive((i) => (i + by + matches.length) % matches.length);
  const onFindKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      step(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      setFind("");
    }
  };
  const jump = (p: LibraryPassage) => {
    setFind("");
    setPassage(locate(reader.flat, p.text));
  };

  const { data } = doc;
  const shownPassages = (passages.data ?? [])
    .filter((p) => locate(reader.flat, p.text))
    .slice(0, 4);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="research-reader">
      <PaneHeader
        title="Document"
        actions={
          <div className="flex items-center gap-0.5">
            {data ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label="Copy Citation"
                    onClick={() => copy(data)}
                    data-testid="reader-copy"
                  >
                    <Copy />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Copy Citation</TooltipContent>
              </Tooltip>
            ) : null}
            {data?.url ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon-xs" variant="ghost" asChild>
                    <a
                      href={data.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open Source"
                    >
                      <ExternalLink />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Open Source</TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Close Document"
                  onClick={onClose}
                  data-testid="reader-close"
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          </div>
        }
        className="border-b border-line-2"
      />

      {doc.isPending ? (
        <div className="flex flex-col gap-3 px-6 py-5">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="mt-4 h-64 w-full" />
        </div>
      ) : doc.isError || !data ? (
        <EmptyState
          icon={FileX}
          title="Couldn't Open the Document"
          description={errorText(doc.error)}
          action={
            <Button size="sm" onClick={() => void doc.refetch()}>
              Try Again
            </Button>
          }
          className="flex-1"
        />
      ) : (
        <>
          <div className="flex shrink-0 flex-col gap-2 border-b border-line-1 px-6 pt-4 pb-3">
            <h2
              className="text-lg font-medium text-fg"
              data-testid="reader-title"
            >
              {data.title}
            </h2>
            <div className="flex flex-wrap items-center gap-1.5">
              <WeightBadge weight={data.weight} />
              {data.citation && !data.title.includes(data.citation) ? (
                <Tag mono>{data.citation}</Tag>
              ) : null}
              <MetaLine
                parts={[
                  courtLabel(data),
                  data.date ? `Decided ${data.date}` : null,
                  data.publisher,
                  data.as_of ? `As of ${data.as_of}` : null
                ]}
              />
            </div>
            {data.text_available && reader.paragraphs.length ? (
              <div className="flex items-center gap-2 pt-1">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-3" />
                  <input
                    value={find}
                    onChange={(e) => setFind(e.currentTarget.value)}
                    onKeyDown={onFindKey}
                    placeholder="Find in document"
                    aria-label="Find in Document"
                    data-testid="reader-find"
                    className="h-7 w-full rounded-md border border-line-3 bg-transparent pr-2 pl-7 text-base text-fg outline-none placeholder:text-fg-4 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40"
                  />
                </div>
                {find.trim().length >= 2 ? (
                  <span
                    className="shrink-0 text-sm text-fg-3 tabular-nums"
                    data-testid="reader-find-count"
                  >
                    {matches.length
                      ? `${active + 1} of ${matches.length}`
                      : "No matches"}
                  </span>
                ) : null}
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Previous Match"
                  disabled={!matches.length}
                  onClick={() => step(-1)}
                >
                  <ChevronUp />
                </Button>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Next Match"
                  disabled={!matches.length}
                  onClick={() => step(1)}
                >
                  <ChevronDown />
                </Button>
              </div>
            ) : null}
          </div>

          <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
            {shownPassages.length ? (
              <section
                className="mx-auto flex w-full max-w-170 flex-col gap-1 px-6 pt-4"
                data-testid="reader-passages"
              >
                <span className="text-sm text-fg-3">Passages on “{query}”</span>
                {shownPassages.map((p, i) => (
                  <button
                    key={`${p.start}-${i}`}
                    type="button"
                    onClick={() => jump(p)}
                    className={cn(
                      "-mx-2 flex gap-2 rounded-md px-2 py-1.5 text-left text-sm text-fg-2 hover:bg-fill-1 hover:text-fg"
                    )}
                  >
                    <Quote className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                    <span className="line-clamp-2">{p.text.trim()}</span>
                  </button>
                ))}
              </section>
            ) : null}
            {data.scan_only ? (
              <p className="mx-auto mt-4 w-full max-w-170 px-6 text-sm text-warn">
                This document is a scan; its text may be incomplete. Check the
                source before you quote it.
              </p>
            ) : null}
            {data.text_available && reader.paragraphs.length ? (
              <ReaderBody
                reader={reader}
                matches={matches}
                active={active}
                passage={passage}
              />
            ) : (
              <EmptyState
                icon={FileX}
                title="No Text in the Library"
                description="The library has this document's record but not its text. Open the source to read it."
                className="py-16"
              />
            )}
            {data.truncated ? (
              <p className="mx-auto w-full max-w-170 px-6 pb-8 text-sm text-fg-3">
                The document continues past what the reader shows. Open the
                source for the rest.
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
